import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Loader2, XCircle, CheckCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { getConsents, recordConsent, withdrawConsent, getPurposes } from '@/services/dpdpa';

export default function ConsentsPage() {
  const [consents, setConsents] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({ principal_identifier: '', identifier_type: 'email', purpose_id: '', collection_method: 'explicit', collection_point: '', legal_basis: 'consent' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cRes, pRes] = await Promise.all([getConsents(statusFilter || undefined), getPurposes()]);
      setConsents(cRes.consents || []);
      setPurposes((pRes.purposes || []).filter(p => p.status === 'active'));
    } catch (err) {
      toast.error('Failed to load consents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRecord = async () => {
    if (!form.principal_identifier || !form.purpose_id) {
      toast.error('Identifier and Purpose are required');
      return;
    }
    try {
      setSaving(true);
      await recordConsent(form);
      toast.success('Consent recorded');
      setShowDialog(false);
      setForm({ principal_identifier: '', identifier_type: 'email', purpose_id: '', collection_method: 'explicit', collection_point: '', legal_basis: 'consent' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record consent');
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async (id) => {
    if (!window.confirm('Withdraw this consent? This action will be logged.')) return;
    try {
      await withdrawConsent(id);
      toast.success('Consent withdrawn');
      fetchData();
    } catch (err) {
      toast.error('Failed to withdraw consent');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="consents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Consent Records</h1>
          <p className="text-sm opacity-60">Track and manage data principal consents per DPDPA</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="consent-status-filter">
              <Filter className="w-4 h-4 mr-1" /><SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="withdrawn">Withdrawn</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setShowDialog(true)} data-testid="record-consent-btn">
            <Plus className="w-4 h-4 mr-2" /> Record Consent
          </Button>
        </div>
      </div>

      {consents.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50"><p>No consent records found. Record consents for data principals.</p></CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="consents-table">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-3 font-medium opacity-60">Principal</th>
                    <th className="p-3 font-medium opacity-60">Purpose</th>
                    <th className="p-3 font-medium opacity-60">Method</th>
                    <th className="p-3 font-medium opacity-60">Status</th>
                    <th className="p-3 font-medium opacity-60">Given At</th>
                    <th className="p-3 font-medium opacity-60">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {consents.map((c) => (
                    <tr key={c.id} className="border-b hover:bg-black/5 transition-colors" data-testid={`consent-row-${c.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{c.principal_identifier}</div>
                        <div className="text-xs opacity-50">{c.identifier_type}</div>
                      </td>
                      <td className="p-3">
                        <div>{c.purpose_name}</div>
                        <div className="text-xs opacity-50">{c.purpose_code}</div>
                      </td>
                      <td className="p-3 capitalize">{c.collection_method}</td>
                      <td className="p-3">
                        <Badge variant={c.status === 'active' ? 'default' : 'secondary'} className={c.status === 'active' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700'}>
                          {c.status === 'active' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                          {c.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs">{new Date(c.given_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        {c.status === 'active' && (
                          <Button variant="ghost" size="sm" onClick={() => handleWithdraw(c.id)} className="text-red-500 hover:text-red-700" data-testid={`withdraw-consent-${c.id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Withdraw
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Record Consent Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="record-consent-dialog">
          <DialogHeader><DialogTitle>Record Consent</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Data Principal Identifier</Label>
              <Input value={form.principal_identifier} onChange={e => setForm(prev => ({ ...prev, principal_identifier: e.target.value }))} placeholder="user@example.com" data-testid="consent-identifier-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Identifier Type</Label>
                <Select value={form.identifier_type} onValueChange={v => setForm(prev => ({ ...prev, identifier_type: v }))}>
                  <SelectTrigger data-testid="consent-identifier-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="aadhaar_virtual">Aadhaar Virtual ID</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Collection Method</Label>
                <Select value={form.collection_method} onValueChange={v => setForm(prev => ({ ...prev, collection_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="explicit">Explicit</SelectItem>
                    <SelectItem value="opt_in">Opt-In</SelectItem>
                    <SelectItem value="opt_out">Opt-Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Purpose</Label>
              <Select value={form.purpose_id} onValueChange={v => setForm(prev => ({ ...prev, purpose_id: v }))}>
                <SelectTrigger data-testid="consent-purpose-select"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>
                  {purposes.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Collection Point</Label>
              <Input value={form.collection_point} onChange={e => setForm(prev => ({ ...prev, collection_point: e.target.value }))} placeholder="e.g. registration_form, cookie_banner" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleRecord} disabled={saving} data-testid="submit-consent-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Record Consent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
